/**
 * THE canonical strategy next-action state, as this portal renders it.
 *
 * Every value here arrives decided by the backend
 * (GET /api/media-campaigns/:campaignId/strategy-next-action, built by
 * services/sherlocStrategyNextActionService). Nothing in this module derives
 * governance, classifies a failure, or composes advice of its own — that is
 * exactly the habit that produced campaign 50's four simultaneous notices,
 * three of them recommending a regeneration the backend guard would refuse
 * without ever reaching a provider.
 *
 * This file is byte-identical in Cortex and Quantum. If it stops being so,
 * the two portals have started disagreeing again.
 */

export const STRATEGY_NEXT_ACTION_SCHEMA_VERSION = "sherloc_strategy_next_action_v1";

export const STRATEGY_NEXT_ACTION_STATES = Object.freeze({
  GENERATION_RUNNING: "GENERATION_RUNNING",
  GENERATION_BLOCKED_NEEDS_USER_INPUT: "GENERATION_BLOCKED_NEEDS_USER_INPUT",
  GENERATION_BLOCKED_SYSTEM_DEFECT: "GENERATION_BLOCKED_SYSTEM_DEFECT",
  GENERATION_BLOCKED_TERMINAL_REPEAT: "GENERATION_BLOCKED_TERMINAL_REPEAT",
  GENERATION_BLOCKED_INPUT_SCOPE: "GENERATION_BLOCKED_INPUT_SCOPE",
  // A retryable failure of the CURRENT request: model output our own gates
  // refused, or the provider failing the attempt. One manual retry is
  // available; nothing about the inputs or the contract changed. Missing from
  // this list, the backend's answer normalized to null and Quantum showed
  // "Current strategy state unavailable" with every control locked.
  GENERATION_RETRY_AVAILABLE: "GENERATION_RETRY_AVAILABLE",
  // An operator-authorized generation failed on a Cphere-owned
  // generation-contract defect, and the repaired contract is PROVEN available.
  // This is a platform repair retry, never a campaign revision, and it is a
  // state of its own precisely so the two can never be shown as one thing:
  // NSC Incremental Markets (2026-08-26) rendered "a repaired generation
  // contract is available" and "blocked by SUBSTANTIVE DEPENDENCY CHANGED" on
  // the same card, then offered "Regenerate the Strategy".
  //
  // Like GENERATION_RETRY_AVAILABLE before it, a state missing from this list
  // normalizes the whole envelope to null and locks every control — so this
  // entry is what keeps a portal on an older deploy from stranding the
  // operator when the backend starts emitting it.
  CONTRACT_REPAIR_RECOVERY_AVAILABLE: "CONTRACT_REPAIR_RECOVERY_AVAILABLE",
  ALLOCATION_PENDING_APPROVAL: "ALLOCATION_PENDING_APPROVAL",
  STALE_INPUTS_CHANGED: "STALE_INPUTS_CHANGED",
  ARTIFACT_EVIDENCE_REQUIRED: "ARTIFACT_EVIDENCE_REQUIRED",
  GOVERNANCE_REVIEW_REQUIRED: "GOVERNANCE_REVIEW_REQUIRED",
  OPERATOR_DECISION_REQUIRED: "OPERATOR_DECISION_REQUIRED",
  NO_STRATEGY: "NO_STRATEGY",
  READY_FOR_REVIEW: "READY_FOR_REVIEW",
  // The strategy's own approval stands while the CAMPAIGN's approval — the
  // client's authorization to proceed — is still pending. Its own state keeps
  // "Approved" truthful in both directions, and (like the entries above)
  // keeps an older portal from normalizing the backend's answer to null and
  // locking every control when the backend starts emitting it.
  STRATEGY_APPROVED_AWAITING_CAMPAIGN_APPROVAL:
    "STRATEGY_APPROVED_AWAITING_CAMPAIGN_APPROVAL",
  APPROVED: "APPROVED",
});

const asText = (value) => String(value ?? "").trim();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * Fail-closed normalization. An envelope of the wrong schema version, or one
 * missing the state or the notice, is treated as ABSENT — the surface keeps
 * its existing behavior rather than rendering a half-understood governance
 * answer. A portal must never invent the missing half.
 */
export const normalizeStrategyNextAction = (envelope) => {
  const source = isPlainObject(envelope?.nextAction) ? envelope.nextAction : envelope;
  if (!isPlainObject(source)) return null;
  if (source.schemaVersion !== STRATEGY_NEXT_ACTION_SCHEMA_VERSION) return null;
  const state = source.state;
  if (typeof state !== "string") return null;
  if (!state || !Object.prototype.hasOwnProperty.call(STRATEGY_NEXT_ACTION_STATES, state)) {
    return null;
  }
  const notice = isPlainObject(source.notice) ? source.notice : null;
  if (!notice || !asText(notice.headline) || !asText(notice.nextAction)) return null;
  // Eligibility is an independent, explicit server verdict, never inferred
  // from the state token. Malformed/missing eligibility cannot grant approval.
  const eligibility = source.allocationApprovalEligibility;
  const validEligibility = isPlainObject(eligibility) &&
    Object.prototype.hasOwnProperty.call(eligibility, "eligible") &&
    typeof eligibility.eligible === "boolean" &&
    Object.prototype.hasOwnProperty.call(eligibility, "reason") &&
    typeof eligibility.reason === "string" && eligibility.reason.length > 0;
  return validEligibility ? source : {
    ...source,
    allocationApprovalEligibility: { eligible: false, reason: "ALLOCATION_ELIGIBILITY_UNAVAILABLE" },
  };
};

/**
 * The ONE notice this surface renders. Never an array, never composed here,
 * and never accompanied by a second notice about the same strategy.
 */
export const resolveStrategyNextActionNotice = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return null;
  return {
    state: canonical.state,
    // The operator-facing NAME of the state, written by the backend that owns
    // the state. `state` itself is a machine token and is never rendered: a
    // surface with no label draws no badge rather than printing the enum, and
    // neither portal keeps a translation table of its own — that is how the
    // two of them start disagreeing about what a state means.
    stateLabel: asText(canonical.notice.stateLabel).replace(/[.!]$/, "") ===
      asText(canonical.notice.headline).replace(/[.!]$/, "")
      ? null : asText(canonical.notice.stateLabel) || null,
    headline: asText(canonical.notice.headline),
    detail: asText(canonical.notice.detail) === asText(canonical.notice.headline)
      ? "" : asText(canonical.notice.detail),
    nextAction: asText(canonical.notice.nextAction),
    requiresOperatorAction: canonical.notice.requiresOperatorAction === true,
    // A failure the operator cannot act on is informational, not an alarm to
    // act on: it is announced politely rather than as an assertive alert.
    tone:
      canonical.state === STRATEGY_NEXT_ACTION_STATES.APPROVED ||
      canonical.state === STRATEGY_NEXT_ACTION_STATES.READY_FOR_REVIEW
        ? "settled"
        : canonical.notice.requiresOperatorAction === true
          ? "action"
          : "informational",
  };
};

/**
 * When the canonical state is present it is the ONLY strategy notice this
 * surface shows. The legacy per-reason cards stay in the codebase for
 * responses that predate the envelope — they must not stack beside it.
 */
export const shouldSuppressLegacyStrategyNotices = (nextAction) =>
  normalizeStrategyNextAction(nextAction) !== null;

/**
 * Whether the regenerate control may be enabled.
 *
 * Fail-closed on an unreadable envelope is NOT correct here: with no canonical
 * answer the surface keeps its existing behavior, because disabling a control
 * on missing evidence strands an operator just as badly as offering a refused
 * one. With an envelope, the backend's answer is final.
 */
export const isStrategyRegenerationEnabled = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return true;
  return canonical.regeneration?.allowed !== false;
};

/**
 * Why regeneration is unavailable, in the backend's own words. Returns null
 * when it is available or when there is no canonical answer.
 */
export const describeStrategyRegenerationBlock = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical || canonical.regeneration?.allowed !== false) return null;
  const regeneration = canonical.regeneration || {};
  const blocking = isPlainObject(regeneration.blockingOperation)
    ? regeneration.blockingOperation
    : null;
  return {
    reason: asText(regeneration.reason) || "REGENERATION_UNAVAILABLE",
    executionContractVersion: asText(regeneration.executionContractVersion) || null,
    operationReference: blocking ? asText(blocking.supportReference) || null : null,
    failureClass: blocking ? asText(blocking.failureClass) || null : null,
    // Proven by the backend, not assumed: answering this costs no provider or
    // reviewer call at all.
    providerCallsRequired:
      regeneration.providerCallsRequired === 0 ? 0 : regeneration.providerCallsRequired ?? null,
  };
};

/**
 * Binding reconciliation is offered ONLY when the backend has positively
 * proven the drift cosmetic. Absent proof, absent affordance — a stale or
 * failed strategy is never presented as one keystroke from approvable.
 */
export const shouldOfferBindingReconciliation = (approvalRequest) => {
  const reconciliation = approvalRequest?.reconciliation;
  if (!isPlainObject(reconciliation)) return false;
  return reconciliation.offered === true && reconciliation.provenCosmetic === true;
};

/** The backend's stated reason reconciliation is not on offer. */
export const describeBindingReconciliationRefusal = (approvalRequest) => {
  const reconciliation = approvalRequest?.reconciliation;
  if (!isPlainObject(reconciliation) || shouldOfferBindingReconciliation(approvalRequest)) {
    return null;
  }
  return {
    reason: asText(reconciliation.reason) || "NO_RECONCILIATION_EVIDENCE",
    detail: asText(reconciliation.detail),
  };
};

/**
 * The current artifact and the latest ATTEMPT are different things and are
 * never collapsed: campaign 50's approval modal named one operation while its
 * planner card named another, and neither said which was which.
 */
export const resolveStrategyIdentityEvidence = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return null;
  const evidence = isPlainObject(canonical.evidence) ? canonical.evidence : {};
  const current = isPlainObject(evidence.currentArtifact) ? evidence.currentArtifact : {};
  const latest = isPlainObject(evidence.latestOperation) ? evidence.latestOperation : null;
  return {
    currentArtifact: {
      operationReference: asText(current.supportReference) || null,
      planVersion: asText(current.planVersion) || null,
      generatedAt: asText(current.generatedAt) || null,
    },
    latestAttempt: latest
      ? {
          operationReference: asText(latest.supportReference) || null,
          disposition: asText(latest.disposition) || null,
          failureClass: asText(latest.failureClass) || null,
          endedAt: asText(latest.endedAt) || null,
        }
      : null,
    // True when the two are the same row — the only case where naming one is
    // the same as naming the other.
    sameOperation:
      Boolean(current.supportReference) &&
      Boolean(latest?.supportReference) &&
      asText(current.supportReference) === asText(latest.supportReference),
  };
};

/**
 * The repaired-contract recovery this envelope carries, in the backend's own
 * words. Null when the backend did not resolve one.
 *
 * `available` is the backend's verdict, never re-derived here. When it is
 * false the `reason` names the exact proof that was missing, so a surface can
 * explain a refusal instead of showing a disabled control with no cause.
 */
export const describeContractRepairRecovery = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return null;
  const evidence = isPlainObject(canonical.evidence) ? canonical.evidence : {};
  const recovery = isPlainObject(evidence.contractRepairRecovery)
    ? evidence.contractRepairRecovery
    : null;
  if (!recovery) return null;
  const failed = isPlainObject(recovery.failedOperation) ? recovery.failedOperation : {};
  return {
    available: recovery.available === true,
    reason: asText(recovery.reason) || null,
    repairedContractVersion: asText(recovery.repairedContractVersion) || null,
    maxRecoveryDepth:
      Number.isFinite(Number(recovery.maxRecoveryDepth))
        ? Number(recovery.maxRecoveryDepth)
        : null,
    failedOperation: {
      operationId: asText(failed.operationId) || null,
      operationReference: asText(failed.supportReference) || null,
      executionContractVersion: asText(failed.executionContractVersion) || null,
      failureClass: asText(failed.failureClass) || null,
    },
  };
};

/**
 * Whether THIS surface should present the recovery affordance — and it is a
 * recovery, not a regeneration. A surface must read this rather than the
 * regenerate flag: the two actions have different authorizations, different
 * copy, and different limits (a recovery is bounded to exactly one).
 */
export const isContractRepairRecoveryAvailable = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return false;
  return (
    canonical.state === STRATEGY_NEXT_ACTION_STATES.CONTRACT_REPAIR_RECOVERY_AVAILABLE &&
    describeContractRepairRecovery(canonical)?.available === true
  );
};

/**
 * The label for the primary control. A system-repair retry must never be
 * labelled as a campaign regeneration — that is what billed a platform defect
 * to the operator as their own revision.
 */
export const resolveStrategyPrimaryActionLabel = (nextAction) =>
  isContractRepairRecoveryAvailable(nextAction)
    ? "Retry under repaired contract"
    : "Regenerate the strategy";

/**
 * The full-strategy GENERATION REQUEST affordance the backend resolved for
 * this campaign — the answer to "may this surface offer the governed request
 * path right now, and what is it called".
 *
 * It is distinct from `regeneration.allowed`, which is the guard's verdict on
 * regenerating an EXISTING artifact under the current inputs and contract and
 * which the artifact-decision route emits false by construction. Reading that
 * flag as the request permission is how a campaign with no strategy at all
 * (the Action Bible's Christmas CTV 2026, campaign 53, 2026-09-21) was left
 * with a notice saying "Create or import a strategy" and no control to do it.
 *
 * Fail-closed: no envelope, no block, or an unrecognized kind reads as
 * unavailable. The affordance never authorizes anything — the operations
 * route and the generation core decide every request on execution.
 */
export const STRATEGY_GENERATION_REQUEST_KINDS = Object.freeze({
  CREATE_STRATEGY: "CREATE_STRATEGY",
  REFRESH_STRATEGY: "REFRESH_STRATEGY",
});

const UNAVAILABLE_GENERATION_REQUEST = Object.freeze({
  available: false,
  kind: null,
  label: null,
  reason: "GENERATION_REQUEST_UNAVAILABLE",
});

export const describeStrategyGenerationRequest = (nextAction) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical) return { ...UNAVAILABLE_GENERATION_REQUEST, reason: "NEXT_ACTION_UNAVAILABLE" };
  const request = isPlainObject(canonical.generationRequest) ? canonical.generationRequest : null;
  // An older backend that does not resolve the affordance yet: absent, not
  // guessed at.
  if (!request) return { ...UNAVAILABLE_GENERATION_REQUEST, reason: "GENERATION_REQUEST_NOT_ADVERTISED" };
  const kind = asText(request.kind);
  const known = Object.prototype.hasOwnProperty.call(STRATEGY_GENERATION_REQUEST_KINDS, kind);
  return {
    available: request.available === true && known,
    kind: known ? kind : null,
    label: asText(request.label) || null,
    reason: asText(request.reason) || UNAVAILABLE_GENERATION_REQUEST.reason,
  };
};

/**
 * Whether the backend advertised the affordance for THIS campaign. The
 * campaign identity check is part of the verdict: an envelope for another
 * campaign (a stale read after a switch) offers nothing here.
 */
export const isStrategyGenerationRequestAvailable = (nextAction, campaignId) => {
  const canonical = normalizeStrategyNextAction(nextAction);
  if (!canonical || String(canonical.campaignId) !== String(campaignId ?? "")) return false;
  return describeStrategyGenerationRequest(canonical).available;
};

/** Absolute path of the canonical read, so neither portal invents its own. */
export const buildStrategyNextActionPath = (campaignId) =>
  `/api/media-campaigns/${encodeURIComponent(String(campaignId ?? ""))}/strategy-next-action`;
